import { useState, useEffect } from "react";
import { Shield, Send, ArrowLeft, CheckCircle, AlertTriangle, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const DMCAPolicy = () => {
  const [form, setForm] = useState({
    full_name: "", email: "", company: "", content_url: "",
    original_work: "", description: "", sworn_statement: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [biz, setBiz] = useState({});

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/business-info`).then(r => r.ok ? r.json() : {}).then(setBiz).catch(() => {});
  }, []);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.sworn_statement) {
      setError("You must confirm the sworn statement.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dmca/takedown`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to submit");
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="DMCA Policy" description="DMCA takedown policy for DJsandMCMedia™. Learn about copyright compliance and how to submit a DMCA notice." noindex={true} />
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-20">
        {/* Page Header */}
        <div className="mb-12">
          <Link to="/" data-testid="dmca-back-link" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 transition text-sm mb-6">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="text-purple-400" size={32} />
            <h1 data-testid="dmca-page-title" className="text-4xl sm:text-5xl font-bold">DMCA Policy</h1>
          </div>
          <p className="text-white/50 text-base">
            Digital Millennium Copyright Act Notice &amp; Takedown Procedures
          </p>
        </div>

        {/* DMCA Badge */}
        <div data-testid="dmca-badge" className="flex items-center gap-4 p-5 rounded-xl mb-10" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)", border: "1px solid rgba(139,92,246,0.2)" }}>
          <Shield className="text-purple-400 shrink-0" size={40} />
          <div>
            <p className="text-white font-semibold text-lg">DMCA Protected</p>
            <p className="text-white/50 text-sm">Registration Number: <span className="text-purple-400 font-mono">DMCA-1070621</span></p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="space-y-8 mb-16">
          <Section title="1. Overview">
            DJsandMCMedia™ respects the intellectual property rights of others and expects its users to do the same.
            In accordance with the Digital Millennium Copyright Act of 1998 ("DMCA"), we will respond promptly to
            claims of copyright infringement committed using our service. If you believe that your copyrighted work
            has been copied in a way that constitutes copyright infringement, please submit a notification pursuant
            to the DMCA by providing our Copyright Agent with the following information.
          </Section>

          <Section title="2. DMCA Registration">
            <p>DJsandMCMedia™ is registered with the U.S. Copyright Office under the DMCA.</p>
            <p className="mt-2"><strong className="text-white">Registration Number:</strong> <span className="text-purple-400 font-mono">DMCA-1070621</span></p>
          </Section>

          <Section title="3. Filing a DMCA Takedown Notice">
            To file a copyright infringement notification with us, you will need to send a written communication
            that includes the following:
            <ul className="list-disc list-inside mt-3 space-y-2 text-white/60">
              <li>A physical or electronic signature of a person authorized to act on behalf of the copyright owner.</li>
              <li>Identification of the copyrighted work claimed to have been infringed.</li>
              <li>Identification of the material that is claimed to be infringing and where it is located on our service.</li>
              <li>Your address, telephone number, and email address.</li>
              <li>A statement that you have a good faith belief that use of the material is not authorized by the copyright owner.</li>
              <li>A statement, under penalty of perjury, that the information in the notification is accurate and that you are authorized to act on behalf of the copyright owner.</li>
            </ul>
          </Section>

          <Section title="4. Counter-Notification">
            If you believe that your content was removed or disabled by mistake or misidentification, you may
            file a counter-notification with us. The counter-notification must include a statement under penalty
            of perjury that you have a good faith belief that the material was removed as a result of mistake or
            misidentification.
          </Section>

          <Section title="5. Repeat Infringers">
            In accordance with the DMCA, DJsandMCMedia™ will terminate, in appropriate circumstances, users
            who are deemed to be repeat infringers. We may also limit access to our service and/or terminate
            the accounts of any users who infringe any intellectual property rights of others.
          </Section>

          <Section title="6. Contact Information">
            <p>DMCA takedown requests should be submitted using the form below or sent to:</p>
            <div className="mt-3 p-4 rounded-lg bg-white/5">
              <p className="text-white font-medium">{biz.name || 'DJsandMCMedia™'} Copyright Agent</p>
              {biz.address && <p className="text-white/50 text-sm mt-1">{[biz.address, biz.city, biz.state, biz.zip].filter(Boolean).join(', ')}</p>}
              <p className="text-white/50 text-sm mt-1">Email: {biz.email || 'wnuv98.5@gmail.com'}</p>
              {biz.phone && <p className="text-white/50 text-sm mt-1">Phone: {biz.phone}</p>}
            </div>
          </Section>
        </div>

        {/* Takedown Request Form */}
        <div id="takedown-form" className="rounded-2xl p-8" style={{ background: "linear-gradient(135deg, #111 0%, #1a1a2e 100%)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="flex items-center gap-3 mb-6">
            <FileText className="text-purple-400" size={24} />
            <h2 data-testid="dmca-form-title" className="text-2xl font-bold">Submit a Takedown Request</h2>
          </div>

          {success ? (
            <div data-testid="dmca-success-message" className="text-center py-12">
              <CheckCircle className="mx-auto text-green-400 mb-4" size={48} />
              <h3 className="text-xl font-bold mb-2">Request Submitted</h3>
              <p className="text-white/50">We will review your claim and respond within 24-48 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div data-testid="dmca-form-error" className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <AlertTriangle size={16} /> {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormInput label="Full Name *" id="full_name" value={form.full_name} onChange={(v) => update("full_name", v)} required />
                <FormInput label="Email Address *" id="email" type="email" value={form.email} onChange={(v) => update("email", v)} required />
              </div>

              <FormInput label="Company / Organization" id="company" value={form.company} onChange={(v) => update("company", v)} />
              <FormInput label="URL of Infringing Content *" id="content_url" value={form.content_url} onChange={(v) => update("content_url", v)} required placeholder="https://..." />
              <FormInput label="Description of Original Work *" id="original_work" value={form.original_work} onChange={(v) => update("original_work", v)} required placeholder="Describe your copyrighted work" />

              <div>
                <label htmlFor="description" className="block text-sm text-white/60 mb-1.5">Additional Details *</label>
                <textarea
                  data-testid="dmca-form-description"
                  id="description"
                  rows={4}
                  required
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50 transition resize-none"
                  placeholder="Provide details about the infringement..."
                />
              </div>

              {/* Sworn Statement */}
              <label data-testid="dmca-form-sworn" className="flex items-start gap-3 cursor-pointer p-4 rounded-lg bg-white/5 border border-white/10">
                <input
                  type="checkbox"
                  checked={form.sworn_statement}
                  onChange={(e) => update("sworn_statement", e.target.checked)}
                  className="mt-1 accent-purple-500"
                />
                <span className="text-sm text-white/60 leading-relaxed">
                  I swear, under penalty of perjury, that the information in this notification is accurate
                  and that I am the copyright owner or am authorized to act on behalf of the owner of an
                  exclusive right that is allegedly infringed.
                </span>
              </label>

              <button
                data-testid="dmca-form-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-medium transition disabled:opacity-50"
              >
                <Send size={16} />
                {submitting ? "Submitting..." : "Submit Takedown Request"}
              </button>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
    <div className="text-white/60 leading-relaxed">{children}</div>
  </section>
);

const FormInput = ({ label, id, type = "text", value, onChange, required, placeholder }) => (
  <div>
    <label htmlFor={id} className="block text-sm text-white/60 mb-1.5">{label}</label>
    <input
      data-testid={`dmca-form-${id}`}
      id={id}
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500/50 transition"
    />
  </div>
);

export default DMCAPolicy;
